import { escapeHTML } from '../../utils/escape.js';
import { formatFechaHora } from '../../utils/formatters.js';
import { ANOMALY_TYPES } from '../../shared/timesheet-policy.js';

/**
 * Cuerpo del cajón "N anomalías" (Unit S3, design.md AD7, spec
 * `payroll-period-dashboard` — "Inline Anomalies Drawer"). Función de render
 * pura — escapa TODOS sus propios valores interpolados (AD7: el shell
 * (`DrawerShell.js`) sólo escapa el título, que `js/admin.js` arma como
 * `"{nombre} · N anomalías"`).
 *
 * Un card por fichada flagged por `detectAnomalies` (vía
 * `liquidacion-tablero-policy.js#anomaliasDeEmpleado`, MISMO llamado que
 * clasificó la fila "N anomalías" en el tablero): `ABIERTA` ofrece "Cerrar
 * turno" (sólo `salida`, motivo obligatorio — mismo criterio que
 * `cerrarFichada`: `salida_at` vacío deja que la RPC use `now()`);
 * `DURACION_EXCESIVA`/`INVERTIDA` ofrecen "Corregir horas" (`entrada` +
 * `salida`, motivo obligatorio) — MISMAS reglas de campo que
 * `FichadaEditDialog.js` (datetime-local, sólo motivo con `required`),
 * reimplementadas acá como formularios INLINE por card (mismo patrón que el
 * sub-form "Anular" de `AdelantoDrawerView.js`) en vez de anidar el diálogo
 * modal de Fichadas dentro de este cajón.
 *
 * `js/admin.js` reusa `admin-fichadas.service.js#cerrarFichada`/`editarFichada`
 * — el mismo servicio que la pestaña de Fichadas.
 *
 * @param {{
 *   cargando: boolean,
 *   anomalias: Array<{fichadaId:string, tipo:string, entrada_at:string|null, salida_at:string|null, minutosTotal:number}>,
 *   error?: string|null,
 *   corrigiendoId?: string|null,
 *   corrigiendoEntrada?: string,
 *   corrigiendoSalida?: string,
 *   corrigiendoMotivo?: string,
 *   corregirError?: string|null,
 *   submitting?: boolean,
 * }} model
 * @returns {string}
 */

/** Same readable labels as `FichadasView.js` (a local copy, not moved to a shared module yet). */
const ANOMALY_LABELS = {
    [ANOMALY_TYPES.ABIERTA]: 'Jornada abierta hace más de 12 h',
    [ANOMALY_TYPES.DURACION_EXCESIVA]: 'Turno de más de 16 horas',
    [ANOMALY_TYPES.INVERTIDA]: 'Horario de salida anterior a la entrada',
};

const BTN = 'px-3 py-2 rounded-xl bg-brand text-white text-[11px] font-black uppercase tracking-widest shadow disabled:opacity-60 disabled:pointer-events-none';
const BTN_SECONDARY =
    'px-3 py-1.5 rounded-xl border border-border-subtle text-text-muted text-[10px] font-black uppercase tracking-widest hover:text-text-primary disabled:opacity-60 disabled:pointer-events-none';
const INPUT = 'w-full bg-base border border-border-subtle rounded-xl px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-brand';

/** Same helper as `FichadaEditDialog.js#toDatetimeLocalValue` (a local copy, not yet moved to a shared module the way `formatFechaHora` moved to `utils/formatters.js`). @param {string|null|undefined} iso @returns {string} */
function toDatetimeLocalValue(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** @param {object} a anomalía @param {object} model @returns {string} */
function renderCerrarForm(a, model) {
    const salida = model.corrigiendoId === a.fichadaId ? (model.corrigiendoSalida ?? '') : toDatetimeLocalValue(a.salida_at);
    const motivo = model.corrigiendoId === a.fichadaId ? (model.corrigiendoMotivo ?? '') : '';
    return `
        <form data-action-form="tablero-anomalia-cerrar-confirmar" data-id="${escapeHTML(a.fichadaId)}" class="mt-3 space-y-2">
            <label class="block text-[10px] font-black uppercase tracking-widest text-text-muted">
                Salida
                <input type="datetime-local" name="salida_at" value="${escapeHTML(salida)}" class="${INPUT} mt-1" />
            </label>
            <label class="block text-[10px] font-black uppercase tracking-widest text-text-muted">
                Motivo
                <input type="text" name="motivo" required value="${escapeHTML(motivo)}" class="${INPUT} mt-1" />
            </label>
            <div class="flex gap-2 pt-1">
                <button type="submit" class="${BTN}" ${model.submitting ? 'disabled' : ''}>Confirmar</button>
                <button type="button" data-action="tablero-anomalia-cancelar" class="${BTN_SECONDARY}" ${model.submitting ? 'disabled' : ''}>Cancelar</button>
            </div>
        </form>
    `;
}

/** @param {object} a anomalía @param {object} model @returns {string} */
function renderCorregirForm(a, model) {
    const propio = model.corrigiendoId === a.fichadaId;
    const entrada = propio ? (model.corrigiendoEntrada ?? '') : toDatetimeLocalValue(a.entrada_at);
    const salida = propio ? (model.corrigiendoSalida ?? '') : toDatetimeLocalValue(a.salida_at);
    const motivo = propio ? (model.corrigiendoMotivo ?? '') : '';
    return `
        <form data-action-form="tablero-anomalia-corregir-confirmar" data-id="${escapeHTML(a.fichadaId)}" class="mt-3 space-y-2">
            <label class="block text-[10px] font-black uppercase tracking-widest text-text-muted">
                Entrada
                <input type="datetime-local" name="entrada_at" value="${escapeHTML(entrada)}" class="${INPUT} mt-1" />
            </label>
            <label class="block text-[10px] font-black uppercase tracking-widest text-text-muted">
                Salida
                <input type="datetime-local" name="salida_at" value="${escapeHTML(salida)}" class="${INPUT} mt-1" />
            </label>
            <label class="block text-[10px] font-black uppercase tracking-widest text-text-muted">
                Motivo
                <input type="text" name="motivo" required value="${escapeHTML(motivo)}" class="${INPUT} mt-1" />
            </label>
            <div class="flex gap-2 pt-1">
                <button type="submit" class="${BTN}" ${model.submitting ? 'disabled' : ''}>Confirmar</button>
                <button type="button" data-action="tablero-anomalia-cancelar" class="${BTN_SECONDARY}" ${model.submitting ? 'disabled' : ''}>Cancelar</button>
            </div>
        </form>
    `;
}

/** @param {object} a anomalía @param {object} model @returns {string} */
function renderCard(a, model) {
    const label = ANOMALY_LABELS[a.tipo] ?? a.tipo;
    const esCierre = a.tipo === ANOMALY_TYPES.ABIERTA;
    const abierta = model.corrigiendoId === a.fichadaId;
    const cardError = abierta && model.corregirError ? `<p role="alert" class="text-xs text-red-500 font-bold mt-2">${escapeHTML(model.corregirError)}</p>` : '';
    // review-bd6afa990ae9d874 (S3 correction, finding 1b): disabled while ANY
    // card is `submitting` (not just this one) — the real stop is
    // `handleAnomaliaAbrirClick`'s `tableroDrawerSubmitting` no-op guard in
    // js/admin.js; this is defense in depth so the OTHER cards' triggers
    // don't even look clickable while a submit is in flight.
    const disabledAttr = model.submitting ? 'disabled' : '';
    const trigger = esCierre
        ? `<button type="button" data-action="tablero-anomalia-cerrar" data-id="${escapeHTML(a.fichadaId)}" class="${BTN_SECONDARY}" ${disabledAttr}>Cerrar turno</button>`
        : `<button type="button" data-action="tablero-anomalia-corregir" data-id="${escapeHTML(a.fichadaId)}" class="${BTN_SECONDARY}" ${disabledAttr}>Corregir horas</button>`;
    const form = abierta ? (esCierre ? renderCerrarForm(a, model) : renderCorregirForm(a, model)) : trigger;

    return `
        <li data-anomalia data-fichada-id="${escapeHTML(a.fichadaId)}" data-tipo="${escapeHTML(a.tipo)}" class="py-3 border-b border-border-subtle last:border-0">
            <span class="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-black uppercase tracking-widest">${escapeHTML(label)}</span>
            <p class="text-xs text-text-muted mt-2">
                Entrada: ${formatFechaHora(a.entrada_at)} · Salida: ${a.salida_at ? formatFechaHora(a.salida_at) : 'sin cierre'}
            </p>
            ${cardError}
            ${form}
        </li>
    `;
}

export function AnomaliasDrawerView(model = {}) {
    const anomalias = model.anomalias ?? [];

    if (model.cargando) {
        return `<p class="text-xs text-text-muted">Cargando...</p>`;
    }

    if (model.error) {
        return `<div role="alert" class="px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-bold">${escapeHTML(model.error)}</div>`;
    }

    if (!anomalias.length) {
        return `<p class="text-xs text-text-muted">Sin anomalías pendientes.</p>`;
    }

    return `
        <div data-drawer-tipo="anomalias">
            <ul>${anomalias.map((a) => renderCard(a, model)).join('')}</ul>
        </div>
    `;
}
